import { Button } from '@tempo-io/sui-button';
import { ButtonGroup } from '@tempo-io/sui-buttongroup';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tempo-io/sui-dropdownmenu';
import { Input, KeyboardArrowDown, Output } from '@tempo-io/sui-icons';
import { Spinner } from '@tempo-io/sui-spinner';
import type { ReactNode } from 'react';
import { SyncType } from '@/common/api/types/workIntegrations';

export interface SyncSplitButtonLabels {
  action: string;
  options: string;
  pull: string;
  push: string;
}

interface SyncSplitButtonProps {
  labels: SyncSplitButtonLabels;
  primarySyncType: SyncType;
  onSelect: (syncType: SyncType) => void;
  disabled: boolean;
  inProgress: boolean;
  unavailableReason?: string;
  testIdPrefix: string;
  actionIcon?: ReactNode;
  hidePushOption?: boolean;
}

const ALTERNATE_TO: Record<SyncType, { syncType: SyncType; icon: ReactNode; testId: string }> = {
  [SyncType.PULL]: { syncType: SyncType.PUSH, icon: <Output />, testId: 'push' },
  [SyncType.PUSH]: { syncType: SyncType.PULL, icon: <Input />, testId: 'pull' },
};

export const SyncSplitButton = ({
  labels,
  primarySyncType,
  onSelect,
  disabled,
  inProgress,
  unavailableReason,
  testIdPrefix,
  actionIcon,
  hidePushOption = false,
}: SyncSplitButtonProps) => {
  const alternate = ALTERNATE_TO[primarySyncType];
  const alternateLabel = alternate.syncType === SyncType.PULL ? labels.pull : labels.push;
  const hidePrimary = hidePushOption && primarySyncType === SyncType.PUSH;
  const hideAlternate = hidePushOption && alternate.syncType === SyncType.PUSH;

  if (hidePrimary) {
    return null;
  }

  const primaryButton = (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      onClick={() => onSelect(primarySyncType)}
      title={unavailableReason}
      data-testid={`${testIdPrefix}-button`}
    >
      <Spinner isLoading={inProgress} size={14} color="inherit">
        <span className="flex items-center gap-1.5">
          {actionIcon}
          {labels.action}
        </span>
      </Spinner>
    </Button>
  );

  if (hideAlternate) {
    return primaryButton;
  }

  return (
    <ButtonGroup>
      {primaryButton}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            title={unavailableReason}
            aria-label={labels.options}
            data-testid={`${testIdPrefix}-options`}
          >
            <KeyboardArrowDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => onSelect(alternate.syncType)}
            data-testid={`${testIdPrefix}-${alternate.testId}`}
          >
            {alternate.icon}
            {alternateLabel}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  );
};
